/**
 * VehiclesApp — Voertuigen & Brandstofverbruik.
 * Tabs (URL ?tab=): Dashboard · Kilometers · Voertuigen.
 * - Dashboard: km/brandstof-grafieken per jaar (kleuren uit het voertuig-type).
 * - Kilometers: maand × voertuig invoer + notities.
 * - Voertuigen: dezelfde kaarten + popups als materiaal-beheer (gedeelde componenten).
 * Export: landscape-PDF met jaar-/voertuig-/taalkeuze (server-side gerenderd).
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, CircularProgress, Dialog, DialogContent, TextField, Checkbox } from '@mui/material';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { BACKEND_URL } from '../../utils/apiConfig';
import PageHeader from '../../ui-components/PageHeader';
import { formatNL, formatInt, MONTH_NAMES_NL } from '../../utils/formatters';
import { TypeCard } from '../MateriaalBeheer/MateriaalBeheer';
import TypePopup from '../MateriaalBeheer/TypePopup';
import ExemplaarPopup from '../MateriaalBeheer/ExemplaarPopup';
import MileageEditor from './MileageEditor';

const T = {
  primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd',
  tertiary: '#635983', surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5', amber: '#f59e0b', error: '#a83836',
  font: '"Manrope", system-ui, sans-serif', fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const ARCHIVED_STATUS = 'Verkocht/gearchiveerd';
const VEHICLE_CATEGORY = 'Vervoer en karren';
const FALLBACK_PALETTE = ['#3aa0ff', '#ffb020', '#ff5c8a', '#22c55e', '#a855f7', '#ff7a45', '#14b8c4'];
// Lichtheid-offsets voor voertuigen die dezelfde type-kleur delen (item 3)
const SHADE_OFFSETS = [0, 0.26, -0.22, 0.44, -0.38, 0.6];

const Icon = ({ name, size = 22, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", lineHeight: 1, display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', ...sx }}>{name}</span>
);

function shade(hex, pct) {
  if (!hex || typeof hex !== 'string' || hex[0] !== '#') return hex || '#526075';
  if (!pct) return hex;
  let c = hex.slice(1);
  if (c.length === 3) c = c.split('').map((x) => x + x).join('');
  const n = parseInt(c, 16);
  let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const f = (v) => Math.round(pct < 0 ? v * (1 + pct) : v + (255 - v) * pct);
  [r, g, b] = [f(r), f(g), f(b)].map((v) => Math.max(0, Math.min(255, v)));
  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

// ── UI-bouwstenen ─────────────────────────────────────────────────────────────
function Card({ children, sx }) {
  return <Box sx={{ bgcolor: T.surface, border: '1px solid rgba(45,51,53,0.07)', borderRadius: '1rem', boxShadow: T.shadow, ...sx }}>{children}</Box>;
}
function SectionLabel({ title, sub, right }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
      <Box>
        <Typography sx={{ fontFamily: T.font, fontSize: '0.98rem', fontWeight: 800, color: T.text }}>{title}</Typography>
        {sub && <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>{sub}</Typography>}
      </Box>
      {right}
    </Box>
  );
}
function Btn({ children, onClick, variant = 'primary', icon, disabled }) {
  const styles = {
    primary: { background: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`, color: '#fff', boxShadow: '0 4px 12px rgba(99,89,131,0.22)' },
    ghost: { background: T.surfaceLow, color: T.secondary },
  }[variant];
  return (
    <Box onClick={disabled ? undefined : onClick} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 2, py: 0.8, borderRadius: '0.55rem', cursor: disabled ? 'default' : 'pointer', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.78rem', userSelect: 'none', opacity: disabled ? 0.55 : 1, transition: 'filter 0.15s', '&:hover': { filter: disabled ? 'none' : 'brightness(0.97)' }, ...styles }}>
      {icon && <Icon name={icon} size={16} />}{children}
    </Box>
  );
}

// Sales-stijl segmented tabs (item 5)
function SegTabs({ value, onChange, tabs }) {
  return (
    <Box sx={{ display: 'inline-flex', background: T.surfaceLow, borderRadius: '10px', p: '3px', gap: '2px' }}>
      {tabs.map((t) => {
        const active = value === t.id;
        return (
          <Box key={t.id} onClick={() => onChange(t.id)} sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.6, cursor: 'pointer',
            px: '14px', py: '8px', borderRadius: '8px', fontFamily: T.fontBody, fontSize: '13px',
            fontWeight: active ? 600 : 400, color: active ? T.text : T.secondary,
            background: active ? '#fff' : 'transparent', boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.15s',
          }}>
            <Icon name={t.icon} size={15} sx={{ color: active ? T.primary : T.outline }} />{t.label}
          </Box>
        );
      })}
    </Box>
  );
}

export default function VehiclesApp() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = ['kilometers', 'voertuigen'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'dashboard';
  const setTab = useCallback((id) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    if (id === 'dashboard') next.delete('tab'); else next.set('tab', id);
    return next;
  }, { replace: true }), [setSearchParams]);

  const [records, setRecords] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('all');
  const [materials, setMaterials] = useState([]);
  const [materiaalTypes, setMateriaalTypes] = useState([]);
  const [materiaalSets, setMateriaalSets] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [openTypeKey, setOpenTypeKey] = useState(null);
  const [openExemplaarId, setOpenExemplaarId] = useState(null);
  const [newVehicle, setNewVehicle] = useState(null); // {name} of null
  const [exp, setExp] = useState(null); // export-dialog state

  // ── Data laden ────────────────────────────────────────────────────────────────
  const loadMileage = useCallback(async () => {
    const r = await axios.get(`${BACKEND_URL}/Api/vehicles-mileage`);
    setRecords(r.data?.records || []);
    const ys = r.data?.years || [];
    setYears(ys);
    setSelectedYear((prev) => (prev !== 'all' && !ys.includes(prev) ? (ys[ys.length - 1] ?? 'all') : prev));
  }, []);
  const loadMateriaal = useCallback(async () => {
    const [m, t, s, res] = await Promise.all([
      axios.get(`${BACKEND_URL}/Api/materialen?includeArchived=true`),
      axios.get(`${BACKEND_URL}/Api/materiaaltypes`),
      axios.get(`${BACKEND_URL}/Api/materiaalsets`),
      axios.get(`${BACKEND_URL}/Api/reservations`).catch(() => ({ data: [] })),
    ]);
    setMaterials(m.data || []);
    setMateriaalTypes(t.data?.records || t.data || []);
    setMateriaalSets(s.data?.records || s.data || []);
    setReservations(res.data || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { await Promise.all([loadMileage(), loadMateriaal()]); }
      catch (e) { toast.error('Laden mislukt: ' + (e.response?.data?.error || e.message)); }
      finally { setLoading(false); }
    })();
  }, [loadMileage, loadMateriaal]);

  const refetchMateriaal = useCallback(() => loadMateriaal(), [loadMateriaal]);

  const typesById = useMemo(() => Object.fromEntries(materiaalTypes.map((t) => [t.id, t])), [materiaalTypes]);

  // Voertuig-exemplaren (kolommen/series/kleuren). Kleur = type-kleur, met lichtheid-variatie
  // voor exemplaren die dezelfde type-kleur delen (item 3).
  const vehicleList = useMemo(() => {
    const list = materials
      .filter((m) => {
        const isVeh = m.fields?.['Is dit een voertuig?'] || m.fields?.Categorie === VEHICLE_CATEGORY;
        if (!isVeh || m.fields?.Status === ARCHIVED_STATUS) return false;
        // Alleen voertuigen waarvan het type brandstofverbruik volgt (item 1)
        const typeFields = typesById[m.fields?.MateriaalType?.[0]]?.fields;
        return typeFields?.TrackBrandstof !== false;
      })
      .map((m) => {
        const typeId = m.fields?.MateriaalType?.[0];
        const base = typesById[typeId]?.fields?.Kleur || null;
        return { id: m.id, name: m.fields?.ID || m.fields?.Naam || 'Voertuig', typeId, base };
      });
    // fallback-kleur voor voertuigen zonder type-kleur
    let pi = 0;
    list.forEach((v) => { if (!v.base) v.base = FALLBACK_PALETTE[pi++ % FALLBACK_PALETTE.length]; });
    // shading per gedeelde kleur
    const byColor = {};
    list.forEach((v) => { (byColor[v.base] = byColor[v.base] || []).push(v); });
    Object.values(byColor).forEach((g) => g.forEach((v, i) => { v.color = shade(v.base, SHADE_OFFSETS[i % SHADE_OFFSETS.length]); }));
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [materials, typesById]);

  const colorFor = useCallback((id) => vehicleList.find((v) => v.id === id)?.color || T.primary, [vehicleList]);

  // ── Groepering voor de Voertuigen-tab (zelfde als materiaal-beheer, gefilterd) ──
  const vehicleGroups = useMemo(() => {
    const matById = {};
    materials.forEach((m) => { matById[m.id] = m; });
    const groups = materiaalTypes.map((mt) => {
      const tf = mt.fields || {};
      const children = (tf.Materialen || []).map((id) => matById[id]).filter(Boolean);
      return { type: mt, typeFields: tf, children, categorie: tf.Categorie || 'Overig' };
    });
    return groups
      .filter((g) => g.categorie === VEHICLE_CATEGORY)
      .sort((a, b) => {
        const arch = (g) => (g.typeFields.Gearchiveerd ? 1 : 0);
        return arch(a) - arch(b) || (a.typeFields.MateriaalType || '').localeCompare(b.typeFields.MateriaalType || '');
      });
  }, [materials, materiaalTypes]);

  const openGroup = useMemo(() => vehicleGroups.find((g) => g.type.id === openTypeKey) || null, [openTypeKey, vehicleGroups]);
  const openExemplaar = useMemo(() => materials.find((m) => m.id === openExemplaarId) || null, [openExemplaarId, materials]);

  const getMaterialReservations = useCallback((materialId) => (
    reservations.filter((r) => (r.fields?.Materiaal || []).includes(materialId)).map((r) => ({
      id: r.id, start: r.fields?.Start, eind: r.fields?.Eind,
      draaidag: r.fields?.DraaidagNaam?.[0] || r.fields?.DraaidagNaam || 'Onbekend',
    })).sort((a, b) => (a.start || '').localeCompare(b.start || ''))
  ), [reservations]);

  // ── Afgeleide dashboard-data ────────────────────────────────────────────────────
  const periodRecords = useMemo(
    () => (selectedYear === 'all' ? records : records.filter((r) => r.year === selectedYear)),
    [records, selectedYear],
  );
  const totalKm = useMemo(() => periodRecords.reduce((s, r) => s + Number(r.mileage_km || 0), 0), [periodRecords]);
  const totalFuel = useMemo(() => periodRecords.reduce((s, r) => s + Number(r.fuel_liters || 0), 0), [periodRecords]);

  const seriesVehicles = useMemo(() => {
    const withData = new Set(periodRecords.map((r) => r.vehicle_material_id));
    const list = vehicleList.filter((v) => withData.has(v.id));
    return list.length ? list : vehicleList;
  }, [vehicleList, periodRecords]);

  const chartData = useMemo(() => {
    const buckets = selectedYear === 'all'
      ? years.map((y) => ({ key: y, label: String(y) }))
      : MONTH_NAMES_NL.map((m, i) => ({ key: i + 1, label: m.slice(0, 3) }));
    return buckets.map((b) => {
      const row = { label: b.label, total: 0 };
      seriesVehicles.forEach((v) => {
        const km = periodRecords.filter((r) => r.vehicle_material_id === v.id && (selectedYear === 'all' ? r.year === b.key : r.month === b.key))
          .reduce((s, r) => s + Number(r.mileage_km || 0), 0);
        row[v.id] = km; row.total += km;
      });
      return row;
    });
  }, [selectedYear, years, seriesVehicles, periodRecords]);

  const pieData = useMemo(() => seriesVehicles.map((v) => ({
    id: v.id, name: v.name,
    value: periodRecords.filter((r) => r.vehicle_material_id === v.id).reduce((s, r) => s + Number(r.mileage_km || 0), 0),
  })).filter((d) => d.value > 0), [seriesVehicles, periodRecords]);

  // ── Kilometers upsert ────────────────────────────────────────────────────────────
  const upsertMileage = async ({ id, vehicle_material_id, month, year, mileage_km, comments }) => {
    try {
      if (id) await axios.patch(`${BACKEND_URL}/Api/vehicles-mileage/${id}`, { mileage_km, comments });
      else await axios.post(`${BACKEND_URL}/Api/vehicles-mileage`, { vehicle_material_id, month, year, mileage_km, comments });
      await loadMileage();
    } catch (e) { toast.error('Opslaan mislukt: ' + (e.response?.data?.error || e.message)); }
  };

  // ── Nieuw voertuig (= nieuw type + eerste exemplaar, categorie Vervoer) ─────────────
  const createVehicle = async () => {
    const name = (newVehicle?.name || '').trim();
    if (!name) return;
    try {
      const r = await axios.post(`${BACKEND_URL}/Api/materiaaltypes`, { name, category: VEHICLE_CATEGORY, icon: 'directions_car', color: '#3aa0ff' });
      const newTypeId = r.data?.data?.record?.id || r.data?.record?.id;
      if (newTypeId) await axios.post(`${BACKEND_URL}/Api/materialen`, { materiaalTypeId: newTypeId });
      toast.success('Voertuig toegevoegd');
      setNewVehicle(null);
      await refetchMateriaal();
      if (newTypeId) setOpenTypeKey(newTypeId);
    } catch (e) { toast.error('Toevoegen mislukt: ' + (e.response?.data?.error || e.message)); }
  };

  // ── PDF export ────────────────────────────────────────────────────────────────
  const openExport = () => setExp({
    year: selectedYear, language: 'nl',
    vehicleIds: vehicleList.map((v) => v.id),
  });
  const runExport = async () => {
    if (!exp) return;
    setExporting(true);
    try {
      const yr = exp.year;
      const sel = vehicleList.filter((v) => exp.vehicleIds.includes(v.id));
      const inScope = (r) => exp.vehicleIds.includes(r.vehicle_material_id) && (yr === 'all' || r.year === yr);
      const recs = records.filter(inScope);
      const periodType = yr === 'all' ? 'year' : 'month';
      const periods = yr === 'all' ? years : Array.from({ length: 12 }, (_, i) => i + 1);
      const matrix = {};
      sel.forEach((v) => { matrix[v.id] = {}; });
      recs.forEach((r) => {
        const key = yr === 'all' ? r.year : r.month;
        matrix[r.vehicle_material_id][key] = (matrix[r.vehicle_material_id][key] || 0) + Number(r.mileage_km || 0);
      });
      const payload = {
        year: yr, language: exp.language, logoUrl: `${window.location.origin}/ff-logo-black.svg`,
        vehicles: sel.map((v) => ({ id: v.id, name: v.name, color: v.color })),
        periodType, periods, matrix,
        totals: { km: recs.reduce((s, r) => s + Number(r.mileage_km || 0), 0), fuel: recs.reduce((s, r) => s + Number(r.fuel_liters || 0), 0) },
      };
      const { data } = await axios.post(`${BACKEND_URL}/Api/vehicles/export-pdf`, payload);
      const bytes = Uint8Array.from(atob(data.pdfBase64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = data.filename || 'brandstofregistratie.pdf'; a.click();
      URL.revokeObjectURL(url);
      setExp(null);
      toast.success('PDF gedownload');
    } catch (e) { toast.error('PDF mislukt: ' + (e.response?.data?.error || e.message)); }
    finally { setExporting(false); }
  };

  const yearTabs = ['all', ...years];
  const tooltipStyle = { fontFamily: T.fontBody, fontSize: 12, borderRadius: 8, border: '1px solid rgba(45,51,53,0.1)' };

  return (
    <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>
      <PageHeader
        icon="directions_car" title="Voertuigen & Brandstof" subtitle="Kilometers en brandstofverbruik per voertuig"
        accent={{ color: T.emerald, bg: T.emeraldBg }}
        rightActions={<Btn variant="ghost" icon="picture_as_pdf" onClick={openExport} disabled={exporting || loading}>{exporting ? 'Bezig…' : 'Export PDF'}</Btn>}
      />

      <Box sx={{ mb: 3 }}>
        <SegTabs value={tab} onChange={setTab} tabs={[
          { id: 'dashboard', label: 'Dashboard', icon: 'monitoring' },
          { id: 'kilometers', label: 'Kilometers', icon: 'edit_note' },
          { id: 'voertuigen', label: 'Voertuigen', icon: 'directions_car' },
        ]} />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: T.primary }} /></Box>
      ) : (
        <>
          {tab !== 'voertuigen' && (
            <Box sx={{ display: 'flex', gap: 0.5, mb: 3, borderBottom: '1px solid rgba(45,51,53,0.08)', flexWrap: 'wrap' }}>
              {yearTabs.map((y) => (
                <Box key={y} onClick={() => setSelectedYear(y)} sx={{
                  px: 1.8, py: 1, cursor: 'pointer', fontFamily: T.font, fontWeight: 700, fontSize: '0.85rem',
                  color: selectedYear === y ? T.text : T.outline,
                  borderBottom: `2px solid ${selectedYear === y ? T.primary : 'transparent'}`, mb: '-1px',
                }}>{y === 'all' ? 'Alle jaren' : y}</Box>
              ))}
              {yearTabs.length === 1 && <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.outline, alignSelf: 'center', ml: 1 }}>Nog geen data.</Typography>}
            </Box>
          )}

          {tab === 'dashboard' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Card sx={{ p: 2.5 }}>
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700, color: T.secondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Totale kilometers</Typography>
                  <Typography sx={{ fontFamily: T.font, fontSize: '2.2rem', fontWeight: 800, color: T.text, letterSpacing: '-0.02em' }}>{formatInt(totalKm)}</Typography>
                </Card>
                <Card sx={{ p: 2.5 }}>
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700, color: T.secondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Brandstof (WLTP, liters)</Typography>
                  <Typography sx={{ fontFamily: T.font, fontSize: '2.2rem', fontWeight: 800, color: T.text, letterSpacing: '-0.02em' }}>{formatNL(totalFuel)}</Typography>
                </Card>
              </Box>

              <Card sx={{ p: 2.5 }}>
                <SectionLabel title="Gebruik per periode" sub="Kilometers per voertuig, met totaallijn" />
                <ResponsiveContainer width="100%" height={340}>
                  <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(45,51,53,0.08)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontFamily: T.fontBody, fontSize: 11, fill: T.secondary }} tickLine={false} axisLine={{ stroke: 'rgba(45,51,53,0.15)' }} />
                    <YAxis tick={{ fontFamily: T.fontBody, fontSize: 11, fill: T.secondary }} tickLine={false} axisLine={false} tickFormatter={(v) => formatInt(v)} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${formatInt(v)} km`, n]} />
                    <Legend wrapperStyle={{ fontFamily: T.fontBody, fontSize: 12 }} />
                    {seriesVehicles.map((v) => <Bar key={v.id} dataKey={v.id} name={v.name} stackId="km" fill={v.color} maxBarSize={46} />)}
                    <Line type="monotone" dataKey="total" name="Totaal" stroke={T.error} strokeWidth={2} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </Card>

              <Card sx={{ p: 2.5 }}>
                <SectionLabel title="Aandeel per voertuig" sub="Verdeling van de kilometers" />
                {pieData.length ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={(d) => `${((d.value / (totalKm || 1)) * 100).toFixed(1)}%`}>
                        {pieData.map((d) => <Cell key={d.id} fill={colorFor(d.id)} />)}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${formatInt(v)} km`} />
                      <Legend wrapperStyle={{ fontFamily: T.fontBody, fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <Typography sx={{ fontFamily: T.fontBody, color: T.outline, py: 3, textAlign: 'center' }}>Geen kilometers in deze periode.</Typography>}
              </Card>
            </Box>
          )}

          {tab === 'kilometers' && (
            <Box>
              <SectionLabel title={`Kilometers ${selectedYear === 'all' ? '' : selectedYear}`.trim()} sub="Klik op een cel om de kilometerstand per maand in te voeren. Gebruik het notitie-icoon voor opmerkingen." />
              {selectedYear === 'all'
                ? <Typography sx={{ fontFamily: T.fontBody, color: T.outline, p: 2 }}>Kies een specifiek jaar om kilometers in te voeren.</Typography>
                : <MileageEditor year={selectedYear} vehicles={vehicleList} records={periodRecords} onUpsert={upsertMileage} />}
            </Box>
          )}

          {tab === 'voertuigen' && (
            <Box>
              <SectionLabel
                title="Voertuigen"
                sub="Zelfde kaarten en popups als materiaalbeheer — wijzigingen gelden op beide plekken."
                right={<Btn variant="primary" icon="add" onClick={() => setNewVehicle({ name: '' })}>Voertuig</Btn>}
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2 }}>
                {vehicleGroups.map((group, i) => (
                  <TypeCard key={group.type.id} group={group} index={i} dimmed={group.typeFields.TrackBrandstof === false} onClick={() => setOpenTypeKey(group.type.id)} />
                ))}
                {!vehicleGroups.length && <Typography sx={{ fontFamily: T.fontBody, color: T.outline }}>Nog geen voertuigen.</Typography>}
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Gedeelde materiaal-beheer popups */}
      <TypePopup
        open={!!openGroup} group={openGroup} allSets={materiaalSets}
        onClose={() => setOpenTypeKey(null)} onChanged={refetchMateriaal}
        onExemplaarClick={(mat) => setOpenExemplaarId(mat.id)}
        getMaterialReservations={getMaterialReservations}
      />
      <ExemplaarPopup
        open={!!openExemplaar} material={openExemplaar}
        onClose={() => setOpenExemplaarId(null)} onChanged={refetchMateriaal}
        getMaterialReservations={getMaterialReservations}
      />

      {/* Nieuw voertuig */}
      <Dialog open={!!newVehicle} onClose={() => setNewVehicle(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '1rem' } }}>
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ fontFamily: T.font, fontSize: '1.1rem', fontWeight: 800, color: T.text, mb: 2 }}>Nieuw voertuig</Typography>
          <TextField autoFocus fullWidth size="small" label="Naam (bijv. Toyota ProAce)" value={newVehicle?.name || ''}
            onChange={(e) => setNewVehicle({ name: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') createVehicle(); }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0.55rem', fontFamily: T.fontBody } }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Btn variant="ghost" onClick={() => setNewVehicle(null)}>Annuleren</Btn>
            <Btn variant="primary" icon="check" onClick={createVehicle}>Toevoegen</Btn>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Export-dialog: jaar + voertuigen + taal (item 7) */}
      <Dialog open={!!exp} onClose={() => setExp(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '1rem' } }}>
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ fontFamily: T.font, fontSize: '1.1rem', fontWeight: 800, color: T.text, mb: 0.5 }}>PDF exporteren</Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.outline, mb: 2 }}>Landscape-PDF met grafieken en tabel.</Typography>

          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.66rem', fontWeight: 700, color: T.secondary, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.8 }}>Jaar</Typography>
          <Box sx={{ display: 'flex', gap: 0.7, flexWrap: 'wrap', mb: 2 }}>
            {['all', ...years].map((y) => (
              <Box key={y} onClick={() => setExp((p) => ({ ...p, year: y }))} sx={{
                px: 1.4, py: 0.5, borderRadius: '0.45rem', cursor: 'pointer', fontFamily: T.fontBody, fontSize: '0.76rem', fontWeight: 700,
                bgcolor: exp?.year === y ? T.primary : T.surfaceLow, color: exp?.year === y ? '#fff' : T.secondary,
              }}>{y === 'all' ? 'Alle jaren' : y}</Box>
            ))}
          </Box>

          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.66rem', fontWeight: 700, color: T.secondary, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>Voertuigen</Typography>
          <Box sx={{ mb: 2 }}>
            {vehicleList.map((v) => {
              const checked = !!exp?.vehicleIds?.includes(v.id);
              return (
                <Box key={v.id} onClick={() => setExp((p) => ({ ...p, vehicleIds: checked ? p.vehicleIds.filter((x) => x !== v.id) : [...p.vehicleIds, v.id] }))}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', py: 0.2 }}>
                  <Checkbox checked={checked} size="small" sx={{ p: 0.3, color: v.color, '&.Mui-checked': { color: v.color } }} />
                  <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: v.color }} />
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.text }}>{v.name}</Typography>
                </Box>
              );
            })}
          </Box>

          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.66rem', fontWeight: 700, color: T.secondary, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.8 }}>Taal</Typography>
          <Box sx={{ display: 'flex', gap: 0.7, mb: 3 }}>
            {[['nl', 'Nederlands'], ['en', 'Engels']].map(([code, lbl]) => (
              <Box key={code} onClick={() => setExp((p) => ({ ...p, language: code }))} sx={{
                px: 1.4, py: 0.5, borderRadius: '0.45rem', cursor: 'pointer', fontFamily: T.fontBody, fontSize: '0.76rem', fontWeight: 700,
                bgcolor: exp?.language === code ? T.primary : T.surfaceLow, color: exp?.language === code ? '#fff' : T.secondary,
              }}>{lbl}</Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Btn variant="ghost" onClick={() => setExp(null)}>Annuleren</Btn>
            <Btn variant="primary" icon="download" onClick={runExport} disabled={exporting || !exp?.vehicleIds?.length}>{exporting ? 'Bezig…' : 'Download'}</Btn>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
